import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { TID } from "../constants/testIds";
import { toast, Toaster } from "sonner";
import Seo from "../components/Seo";

const empty = {
  student_first_name: "", student_last_name: "", birth_date: "",
  parent_name: "", phone: "", email: "", address: "",
  category: "music", course: "", note: "", privacy_accepted: false,
};

const Anmeldung = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const c = params.get("course");
    const cat = params.get("category");
    setForm(f => ({ ...f, course: c || f.course, category: cat || f.category }));
  }, [params]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accepted) {
      toast.error("Bitte Datenschutz akzeptieren");
      return;
    }
    setLoading(true);
    try {
      await api.post("/public/enroll", form);
      toast.success(t("enroll.success"));
      setForm(empty);
    } catch (err) {
      toast.error("Fehler beim Senden");
    } finally { setLoading(false); }
  };

  const inp = "w-full bg-input/30 border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-primary";
  const lab = "block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2";

  return (
    <div data-testid="enroll-page">
      <Seo
        title="Anmeldung – Kurse, Workshops & Events"
        description="Melde dich für Musik- und Tanzkurse, Workshops und Events der KK Academy an. Schnell und unkompliziert online."
        path="/anmeldung"
      />
      <Toaster position="top-center" theme="light" />
      <section className="py-20 max-w-4xl mx-auto px-6">
        <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">{t("nav.enroll")}</div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] mb-4">{t("enroll.title")}</h1>
        <p className="text-muted-foreground mb-10">{t("enroll.sub")}</p>

        <form onSubmit={submit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className={lab}>{t("enroll.firstName")}</label>
              <input required value={form.student_first_name} onChange={e => set("student_first_name", e.target.value)} data-testid={TID.enrollFirstName} className={inp} />
            </div>
            <div>
              <label className={lab}>{t("enroll.lastName")}</label>
              <input required value={form.student_last_name} onChange={e => set("student_last_name", e.target.value)} data-testid={TID.enrollLastName} className={inp} />
            </div>
            <div>
              <label className={lab}>{t("enroll.birthDate")}</label>
              <input required type="date" value={form.birth_date} onChange={e => set("birth_date", e.target.value)} data-testid={TID.enrollBirthDate} className={inp} />
            </div>
            <div>
              <label className={lab}>{t("enroll.parentName")}</label>
              <input value={form.parent_name} onChange={e => set("parent_name", e.target.value)} data-testid={TID.enrollParentName} className={inp} />
            </div>
            <div>
              <label className={lab}>{t("enroll.phone")}</label>
              <input required value={form.phone} onChange={e => set("phone", e.target.value)} data-testid={TID.enrollPhone} className={inp} />
            </div>
            <div>
              <label className={lab}>{t("enroll.email")}</label>
              <input required type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid={TID.enrollEmail} className={inp} />
            </div>
            <div className="md:col-span-2">
              <label className={lab}>{t("enroll.address")}</label>
              <input required value={form.address} onChange={e => set("address", e.target.value)} data-testid={TID.enrollAddress} className={inp} />
            </div>
            <div>
              <label className={lab}>{t("enroll.category")}</label>
              <select value={form.category} onChange={e => set("category", e.target.value)} data-testid={TID.enrollCategory} className={inp}>
                <option value="music">{t("enroll.music")}</option>
                <option value="dance">{t("enroll.dance")}</option>
                <option value="workshop">{t("enroll.workshop")}</option>
              </select>
            </div>
            <div>
              <label className={lab}>{t("enroll.course")}</label>
              <input required value={form.course} onChange={e => set("course", e.target.value)} data-testid={TID.enrollCourse} className={inp} placeholder="z.B. Miruthangam" />
            </div>
            <div className="md:col-span-2">
              <label className={lab}>{t("enroll.note")}</label>
              <textarea rows={3} value={form.note} onChange={e => set("note", e.target.value)} data-testid={TID.enrollNote} className={inp} />
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.privacy_accepted}
              onChange={e => set("privacy_accepted", e.target.checked)}
              data-testid={TID.enrollPrivacy}
              className="mt-1 accent-[hsl(35_90%_55%)]"
            />
            <span className="text-sm text-muted-foreground">{t("enroll.privacy")}</span>
          </label>

          <button type="submit" disabled={loading} data-testid={TID.enrollSubmit} className="px-8 py-3.5 rounded-full font-medium btn-gold disabled:opacity-50">
            {loading ? "…" : t("enroll.submit")}
          </button>
        </form>
      </section>
    </div>
  );
};

export default Anmeldung;
